import { ApolloError, useMutation } from '@apollo/client';
import { assertNeverType } from '@common/neo.tax';

import {
  AccountingProvider,
  DELETE_ACCOUNTING_TOKENS_BY_COMPANY_AND_PROVIDER,
  DELETE_PAYROLL_TOKENS_BY_COMPANY_AND_PROVIDER,
  GET_COMPANY,
  PayrollProvider,
  DeleteAccountingTokensByCompanyAndProviderMutation,
  DeleteAccountingTokensByCompanyAndProviderMutationVariables,
  DeletePayrollTokensByCompanyAndProviderMutation,
  DeletePayrollTokensByCompanyAndProviderMutationVariables,
  GetCompanyQueryVariables,
} from '@neo.tax/shared/graphql';

type UseDeleteTokensByCompanyAndProviderProps =
  | {
      companyId: string;
      providerName: AccountingProvider;
      providerType: 'accounting';
    }
  | {
      companyId: string;
      providerName: PayrollProvider;
      providerType: 'payroll';
    };

type UseDeleteTokensByCompanyAndProviderResult = [
  () => Promise<string[] | null>,
  { loading: boolean; error?: ApolloError },
];

export const useDeleteTokensByCompanyAndProvider = ({
  companyId,
  providerName,
  providerType,
}: UseDeleteTokensByCompanyAndProviderProps): UseDeleteTokensByCompanyAndProviderResult => {
  const [
    deleteAccountingTokensByCompanyAndProvider,
    { error: accountingTokensError, loading: deletingAccountingToken },
  ] = useMutation<
    DeleteAccountingTokensByCompanyAndProviderMutation,
    DeleteAccountingTokensByCompanyAndProviderMutationVariables
  >(DELETE_ACCOUNTING_TOKENS_BY_COMPANY_AND_PROVIDER, {
    refetchQueries: [
      {
        query: GET_COMPANY,
        variables: { companyId } satisfies GetCompanyQueryVariables,
      },
    ],
    awaitRefetchQueries: true,
  });
  const [deletePayrollTokensByCompanyAndProvider, { error: payrollTokensError, loading: deletingPayrollToken }] =
    useMutation<
      DeletePayrollTokensByCompanyAndProviderMutation,
      DeletePayrollTokensByCompanyAndProviderMutationVariables
    >(DELETE_PAYROLL_TOKENS_BY_COMPANY_AND_PROVIDER, {
      refetchQueries: [
        {
          query: GET_COMPANY,
          variables: { companyId } satisfies GetCompanyQueryVariables,
        },
      ],
      awaitRefetchQueries: true,
    });

  let deleteTokens: () => Promise<string[] | null>;
  switch (providerType) {
    case 'payroll':
      deleteTokens = async (): Promise<string[] | null> => {
        const result = await deletePayrollTokensByCompanyAndProvider({
          variables: {
            companyId,
            payrollProvider: providerName,
          },
        });
        return result.data?.deletePayrollTokensByCompanyAndProvider.ids || null;
      };
      break;
    case 'accounting':
      deleteTokens = async (): Promise<string[] | null> => {
        const result = await deleteAccountingTokensByCompanyAndProvider({
          variables: {
            companyId,
            accountingProvider: providerName,
          },
        });
        return result.data?.deleteAccountingTokensByCompanyAndProvider.ids || null;
      };
      break;
    default:
      assertNeverType(providerType, 'Provider Type');
  }

  return [
    deleteTokens,
    { loading: deletingAccountingToken || deletingPayrollToken, error: accountingTokensError || payrollTokensError },
  ];
};
